import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { useCurrency } from '../context/CurrencyContext';
import { executiveAPI, aiAPI } from '../lib/api';
import { markOnboarding } from '../components/OnboardingChecklist';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Loader2,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  DollarSign,
  Users,
  Clock,
  Building2,
  ChevronUp,
  ChevronDown,
  AlertCircle,
  CheckCircle,
  Activity,
  BarChart3,
  Target,
  Zap,
  RefreshCw,
  CalendarClock,
  Sparkles,
} from 'lucide-react';
import { HelpTooltip } from '../components/HelpTooltip';

// Simple chart components using divs
const TrendChart = ({ data, height = 120 }) => {
  if (!data || data.length === 0) return <div className="text-sm text-slate-400 text-center py-8">No data available</div>;
  
  const maxValue = Math.max(...data.map(d => d.hours), 1);
  
  return (
    <div className="flex items-end gap-1 h-[120px]" style={{ height }}>
      {data.map((item, idx) => (
        <div key={idx} className="flex-1 flex flex-col items-center gap-1">
          <div 
            className="w-full bg-orange-500 rounded-t-sm transition-all duration-300 hover:bg-orange-600"
            style={{ height: `${(item.hours / maxValue) * 100}%`, minHeight: item.hours > 0 ? '4px' : '0' }}
            title={`${item.date}: ${item.hours} hrs`}
          />
          <span className="text-[9px] text-slate-400 truncate w-full text-center">
            {item.date.slice(5)}
          </span>
        </div>
      ))}
    </div>
  );
};

const HealthScoreGauge = ({ score }) => {
  const getColor = (s) => {
    if (s >= 80) return 'text-green-500';
    if (s >= 60) return 'text-yellow-500';
    if (s >= 40) return 'text-orange-500';
    return 'text-red-500';
  };
  
  const getBgColor = (s) => {
    if (s >= 80) return 'bg-green-500';
    if (s >= 60) return 'bg-yellow-500';
    if (s >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };
  
  return (
    <div className="relative w-40 h-40 mx-auto">
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
        <circle
          cx="50" cy="50" r="40"
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="12"
        />
        <circle
          cx="50" cy="50" r="40"
          fill="none"
          stroke="currentColor"
          strokeWidth="12"
          strokeDasharray={`${score * 2.51} 251`}
          strokeLinecap="round"
          className={getColor(score)}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-4xl font-bold ${getColor(score)}`}>{score}</span>
        <span className="text-xs text-slate-500">Health Score</span>
      </div>
    </div>
  );
};

const BudgetVarianceSection = ({ locations }) => {
  const budgetLocations = locations.filter(loc => loc.weekly_budget > 0);
  const { formatMoney } = useCurrency();

  if (budgetLocations.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign size={20} className="text-orange-500" />
            Budget vs Actual
          </CardTitle>
          <CardDescription className="break-words">Configure budgets in Settings to see variance data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <Target className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No budget targets configured yet</p>
            <p className="text-xs text-slate-400 mt-1 break-words">Go to Settings → Edit Location → Labor Budget to set targets</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const overBudgetCount = budgetLocations.filter(l => l.is_over_budget).length;
  const atRiskCount = budgetLocations.filter(l => !l.is_over_budget && l.projected_variance < 0).length;

  return (
    <Card data-testid="budget-variance-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <DollarSign size={20} className="text-orange-500" />
          Budget vs Actual
        </CardTitle>
        <CardDescription>Labor cost variance by location</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* Over-budget risk banner */}
        {overBudgetCount > 0 && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="over-budget-risk-banner">
            <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
            <div>
              <p className="text-sm font-semibold text-red-800">
                {overBudgetCount} location{overBudgetCount > 1 ? 's' : ''} over budget
              </p>
              <p className="text-xs text-red-600 mt-0.5">Immediate action may be required to contain labor costs.</p>
            </div>
          </div>
        )}
        {atRiskCount > 0 && overBudgetCount === 0 && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="at-risk-banner">
            <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={16} />
            <div>
              <p className="text-sm font-semibold text-amber-800">
                {atRiskCount} location{atRiskCount > 1 ? 's' : ''} at risk of exceeding budget this week
              </p>
              <p className="text-xs text-amber-600 mt-0.5">Projected spend is on track to exceed the weekly budget.</p>
            </div>
          </div>
        )}

        {budgetLocations.map((loc) => {
          const overBudget = loc.is_over_budget;
          const varianceAbs = Math.abs(loc.budget_variance);
          const variancePctAbs = Math.abs(loc.budget_variance_percent);
          const barWidth = loc.weekly_budget > 0
            ? Math.min(100, (loc.actual_labor_cost / loc.weekly_budget) * 100)
            : 0;
          const forecastOverBudget = loc.projected_variance < 0 && !overBudget;

          return (
            <div
              key={loc.id}
              className={`p-4 rounded-lg border ${overBudget ? 'border-red-200 bg-red-50/50' : forecastOverBudget ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200 bg-slate-50/50'}`}
              data-testid={`budget-loc-${loc.id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-slate-900 text-sm">{loc.name}</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  overBudget
                    ? 'bg-red-100 text-red-700'
                    : forecastOverBudget
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-green-100 text-green-700'
                }`}>
                  {overBudget ? '⚠ Over Budget' : forecastOverBudget ? '⚠ At Risk' : '✓ On Track'}
                </span>
              </div>

              {/* Progress bar */}
              <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden mb-3">
                <div
                  className={`h-full rounded-full transition-all ${overBudget ? 'bg-red-500' : forecastOverBudget ? 'bg-amber-400' : 'bg-green-500'}`}
                  style={{ width: `${barWidth}%` }}
                />
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs mb-3 min-w-0">
                <div>
                  <p className="text-slate-500">Budget</p>
                  <p className="font-semibold text-slate-800">{formatMoney(loc.weekly_budget)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Actual</p>
                  <p className="font-semibold text-slate-800">{formatMoney(loc.actual_labor_cost)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Variance</p>
                  <p className={`font-semibold flex items-center gap-0.5 ${overBudget ? 'text-red-600' : 'text-green-600'}`}>
                    {overBudget ? <TrendingDown size={12} /> : <TrendingUp size={12} />}
                    {formatMoney(varianceAbs)} ({variancePctAbs.toFixed(1)}%)
                  </p>
                </div>
              </div>

              {/* End-of-week forecast row */}
              <div className={`flex flex-wrap items-start gap-2 p-2.5 rounded-md text-xs ${
                loc.projected_variance < 0 ? 'bg-amber-50 border border-amber-100' : 'bg-slate-100'
              }`}>
                <CalendarClock size={13} className={`flex-shrink-0 mt-0.5 ${loc.projected_variance < 0 ? 'text-amber-500' : 'text-slate-500'}`} />
                <div className="flex-1">
                  <span className="font-medium text-slate-700">End-of-week forecast: </span>
                  <span className={`font-bold ${loc.projected_variance < 0 ? 'text-amber-700' : 'text-green-700'}`}>
                    {formatMoney(loc.projected_weekly_cost)}
                  </span>
                  {loc.projected_variance < 0 && (
                    <span className="text-amber-600 ml-1">
                      (over by {formatMoney(Math.abs(loc.projected_variance))})
                    </span>
                  )}
                </div>
                {loc.actual_labor_percent > 0 && (
                  <div className="text-right">
                    <span className={`font-semibold ${
                      loc.actual_labor_percent > loc.labor_target_percent + 5 ? 'text-red-600' :
                      loc.actual_labor_percent > loc.labor_target_percent ? 'text-amber-600' : 'text-green-600'
                    }`}>
                      {loc.actual_labor_percent.toFixed(1)}%
                    </span>
                    <span className="text-slate-400 ml-0.5">/ {loc.labor_target_percent}% target</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default function ExecutiveInsights() {
  const { user } = useAuth();
  const { selectedLocationId } = useLocation();
  const { formatMoney, reportCurrency } = useCurrency();

  // Mark onboarding step when user opens labor dashboard
  useEffect(() => {
    const uid = user?.id || user?._id || user?.email;
    markOnboarding(uid, 'dashboard');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState('30');
  const [sortField, setSortField] = useState('health_score');
  const [sortDirection, setSortDirection] = useState('desc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [analyzing, setAnalyzing] = useState(false);

  const fetchData = async (showRefreshToast = false) => {
    try {
      if (showRefreshToast) setRefreshing(true);
      else setLoading(true);
      
      let start = startDate;
      let end = endDate;
      
      if (!start || !end) {
        const now = new Date();
        end = now.toISOString();
        const daysAgo = new Date(now.getTime() - parseInt(dateRange) * 24 * 60 * 60 * 1000);
        start = daysAgo.toISOString();
      }

      // Pass selected location to API — "all" or null means all accessible locations
      const locFilter = selectedLocationId && selectedLocationId !== 'all' ? selectedLocationId : null;
      const response = await executiveAPI.getDashboard(start, end, locFilter);
      setData(response.data);
      
      if (showRefreshToast) {
        toast.success('Dashboard refreshed');
      }
    } catch (error) {
      console.error('Error fetching executive dashboard:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied: Executive dashboard is not available for your role');
      } else {
        toast.error('Failed to load executive dashboard');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Re-fetch whenever location switcher or date range changes
  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedLocationId]);

  const handleCustomDateSearch = () => {
    if (startDate && endDate) {
      fetchData();
    }
  };

  const analyzeWithEva = async () => {
    if (!data || analyzing) return;
    setAnalyzing(true);
    try {
      const overtimeHours = (data.locations || []).reduce(
        (sum, loc) => sum + (loc.overtime_hours || 0), 0
      );
      const contextData = {
        weekly_labor_cost: data.profit_impact?.total_labor_cost || 0,
        overtime_hours: parseFloat(overtimeHours.toFixed(1)),
        upcoming_shifts: data.summary?.total_shifts || 0,
        active_staff: data.summary?.active_staff || 0,
      };
      const res = await aiAPI.chat(
        `Analyze my workforce performance for the last ${dateRange} days and give me 3 specific actions I should take immediately.`,
        contextData
      );
      window.dispatchEvent(new CustomEvent('eva:open', { detail: res.data.response }));
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Eva is unavailable. Check your AI settings.';
      toast.error(detail);
    } finally {
      setAnalyzing(false);
    }
  };

  const sortedLocations = useMemo(() => {
    if (!data?.locations) return [];
    
    return [...data.locations].sort((a, b) => {
      const aVal = a[sortField] || 0;
      const bVal = b[sortField] || 0;
      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [data?.locations, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortButton = ({ field, label }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
    >
      {label}
      {sortField === field && (
        sortDirection === 'desc' ? <ChevronDown size={14} /> : <ChevronUp size={14} />
      )}
    </button>
  );

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 border-red-300 text-red-800';
      case 'medium': return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      default: return 'bg-blue-100 border-blue-300 text-blue-800';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high': return <AlertCircle className="text-red-500" size={18} />;
      case 'medium': return <AlertTriangle className="text-yellow-500" size={18} />;
      default: return <Activity className="text-blue-500" size={18} />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="text-center py-12">
          <AlertTriangle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Unable to load executive dashboard</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Executive Insights
              <HelpTooltip text="Executive Insights analyzes workforce performance, labor efficiency, and staffing risk indicators to help managers optimize operations." learnMoreUrl="/help/executive-insights" />
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Labor intelligence dashboard • {data.summary?.total_locations || 0} location{data.summary?.total_locations !== 1 ? 's' : ''}
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium" data-testid="currency-badge">
                Currency: {reportCurrency}
              </span>
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[140px]" data-testid="date-range-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>

            {/* Analyze with Eva — owner/manager only */}
            {['owner', 'manager'].includes(user?.role) && (
              <Button
                variant="outline"
                size="sm"
                onClick={analyzeWithEva}
                disabled={analyzing || !data}
                data-testid="analyze-with-eva-btn"
                className="border-orange-200 text-orange-600 hover:bg-orange-50 hover:text-orange-700 gap-1.5"
              >
                {analyzing
                  ? <Loader2 size={14} className="animate-spin" />
                  : <Sparkles size={14} />
                }
                {analyzing ? 'Analyzing…' : 'Analyze with Eva'}
              </Button>
            )}

            <Button
              variant="outline"
              size="icon"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Top Stats Row */}
        {data.summary?.has_uncosted_staff && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 mb-1" data-testid="uncosted-staff-warning">
            <span className="mt-0.5 shrink-0 text-base">⚠</span>
            <span>
              <strong>{data.summary.uncosted_hours?.toFixed(0)}h</strong> of labor is uncosted — some staff don't have hourly wages set.
              Labor cost totals only reflect employees with wages configured.{' '}
              <a href="/staff" className="underline font-medium hover:text-amber-900">Set wages in Staff →</a>
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Labor Health Score */}
          <Card className="col-span-2 lg:col-span-1">
            <CardContent className="pt-6">
              <HealthScoreGauge score={data.labor_health_score} />
              <p className="text-center text-sm text-slate-500 mt-2">
                {data.labor_health_score >= 80 ? 'Excellent' : 
                 data.labor_health_score >= 60 ? 'Good' :
                 data.labor_health_score >= 40 ? 'Needs Attention' : 'Critical'}
              </p>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{data.summary?.total_hours?.toLocaleString() || 0}</p>
                  <p className="text-xs text-slate-500">Total Hours</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{data.summary?.active_staff || 0}</p>
                  <p className="text-xs text-slate-500">Active Staff</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{data.summary?.total_shifts?.toLocaleString() || 0}</p>
                  <p className="text-xs text-slate-500">Total Shifts</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6 min-w-0">
          {/* Left Column - Location Comparison */}
          <div className="lg:col-span-2 space-y-6 min-w-0">
            {/* Location Comparison Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 size={20} className="text-orange-500" />
                  Location Comparison
                </CardTitle>
                <CardDescription>Click headers to sort</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto -mx-2 px-2">
                  <table className="w-full min-w-[480px] text-sm" data-testid="location-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-2">Location</th>
                        <th className="text-right py-2 px-2">
                          <SortButton field="health_score" label="Score" />
                        </th>
                        <th className="text-right py-2 px-2">
                          <SortButton field="total_hours" label="Hours" />
                        </th>
                        <th className="text-right py-2 px-2">
                          <SortButton field="staff_count" label="Staff" />
                        </th>
                        <th className="text-right py-2 px-2">
                          <SortButton field="overtime_hours" label="OT" />
                        </th>
                        <th className="text-right py-2 px-2">
                          <SortButton field="attendance_rate" label="Attend" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedLocations.map((loc, idx) => (
                        <tr 
                          key={loc.id} 
                          className={`border-b border-slate-100 hover:bg-slate-50 ${idx === 0 ? 'bg-green-50' : ''}`}
                          data-testid={`location-row-${loc.id}`}
                        >
                          <td className="py-3 px-2">
                            <div className="font-medium text-slate-900">{loc.name}</div>
                            <div className="text-xs text-slate-500">{loc.shift_count} shifts</div>
                          </td>
                          <td className="text-right py-3 px-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              loc.health_score >= 80 ? 'bg-green-100 text-green-700' :
                              loc.health_score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                              loc.health_score >= 40 ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {loc.health_score}
                            </span>
                          </td>
                          <td className="text-right py-3 px-2 font-medium">{loc.total_hours.toLocaleString()}</td>
                          <td className="text-right py-3 px-2">{loc.staff_count}</td>
                          <td className="text-right py-3 px-2">
                            <span className={loc.overtime_hours > 10 ? 'text-red-600 font-medium' : ''}>
                              {loc.overtime_hours.toFixed(1)}
                            </span>
                          </td>
                          <td className="text-right py-3 px-2">
                            <span className={loc.attendance_rate < 85 ? 'text-red-600 font-medium' : 'text-green-600'}>
                              {loc.attendance_rate.toFixed(0)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {sortedLocations.length === 0 && (
                  <p className="text-center text-slate-500 py-8">No location data available</p>
                )}
              </CardContent>
            </Card>

            {/* Budget vs Actual Variance */}
            <BudgetVarianceSection locations={sortedLocations} />

            {/* Trend Chart */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp size={20} className="text-orange-500" />
                  Daily Hours Trend
                </CardTitle>
                <CardDescription>Hours worked per day</CardDescription>
              </CardHeader>
              <CardContent>
                <TrendChart data={data.trends?.daily_hours} />
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Alerts & Profit */}
          <div className="space-y-6">
            {/* Risk Alerts */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle size={20} className="text-orange-500" />
                  Risk Alerts
                </CardTitle>
                <CardDescription>
                  {data.risk_alerts?.length || 0} active alert{data.risk_alerts?.length !== 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {data.risk_alerts?.length > 0 ? (
                    data.risk_alerts.map((alert, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg border ${getSeverityColor(alert.severity)}`}
                        data-testid={`alert-${idx}`}
                      >
                        <div className="flex items-start gap-2">
                          {getSeverityIcon(alert.severity)}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{alert.message}</p>
                            <p className="text-xs mt-1 opacity-75">{alert.location}</p>
                            {alert.recommendation && (
                              <p className="text-xs mt-2 italic">💡 {alert.recommendation}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                      <p className="text-sm text-slate-600">No active alerts</p>
                      <p className="text-xs text-slate-400">Your operations are running smoothly</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Profit Impact Estimator */}
            <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-white">
                  <DollarSign size={20} className="text-green-400" />
                  Profit Impact
                </CardTitle>
                <CardDescription className="text-slate-400">Estimated financial metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-red-500/20 rounded">
                        <TrendingDown size={14} className="text-red-400" />
                      </div>
                      <span className="text-sm text-slate-300">Overtime Cost</span>
                    </div>
                    <span className="font-bold text-red-400">
                      {formatMoney(data.profit_impact?.overtime_cost || 0)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-green-500/20 rounded">
                        <Target size={14} className="text-green-400" />
                      </div>
                      <span className="text-sm text-slate-300">Potential Savings</span>
                    </div>
                    <span className="font-bold text-green-400">
                      {formatMoney(data.profit_impact?.estimated_savings || 0)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-500/20 rounded">
                        <Zap size={14} className="text-blue-400" />
                      </div>
                      <span className="text-sm text-slate-300">Efficiency Gain</span>
                    </div>
                    <span className="font-bold text-blue-400">
                      {formatMoney(data.profit_impact?.efficiency_gain || 0)}
                    </span>
                  </div>
                  
                  <div className="pt-3 border-t border-slate-700">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Total Labor Cost</span>
                      <span className="text-xl font-bold text-white" data-testid="total-labor-cost">
                        {formatMoney(data.profit_impact?.total_labor_cost || 0)}
                      </span>
                    </div>
                    {data.profit_impact?.total_labor_cost === 0 && data.summary?.uncosted_hours > 0 && (
                      <p className="text-xs text-slate-400 mt-1 text-right">Set staff wages to see cost</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Custom Date Range */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Custom Date Range</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="start-date" className="text-xs">Start Date</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={startDate.slice(0, 10)}
                      onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value).toISOString() : '')}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="end-date" className="text-xs">End Date</Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={endDate.slice(0, 10)}
                      onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value).toISOString() : '')}
                      className="h-8 text-sm"
                    />
                  </div>
                  <Button 
                    onClick={handleCustomDateSearch} 
                    className="w-full h-8"
                    disabled={!startDate || !endDate}
                    data-testid="custom-date-search-btn"
                  >
                    Apply
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
